/**
 * Assign a training module — training administration only (Central
 * training:write, the same capability the server requires).
 *
 * Assignees are picked from the workspace's members as Central lists them
 * (the Settings → Members list); the server checks each one against Central
 * again before writing. Every new assignment is bound to the module's latest
 * published version, shown here; members who already have this module keep
 * their existing assignment and version.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useCreateAssignmentsMutation,
  useGetTrainingModuleQuery,
  type CreatedAssignment,
} from '../store/api/trainingApi';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { TRAINING_ROUTES, TrainingErrorNotice, VersionLabel } from '../components/training/trainingUi';

export function AssignTrainingModulePage() {
  const { moduleId = '' } = useParams<{ moduleId: string }>();
  const { hasCapability } = useCentralConsumer();
  const canManage = hasCapability('training', 'write');
  const detail = useGetTrainingModuleQuery(moduleId, { skip: !canManage });
  const members = useWorkspaceMembers();
  const [assign, assignState] = useCreateAssignmentsMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [result, setResult] = useState<CreatedAssignment[] | null>(null);

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" data-testid="training-assign-denied">
        <p className="text-gray-700">Assigning training needs the training administration capability in this workspace.</p>
        <Link to={TRAINING_ROUTES.dashboard} className="text-primary-600 hover:underline">
          Back to training
        </Link>
      </div>
    );
  }
  if (detail.isLoading) return <div className="p-6 text-gray-500">Loading…</div>;
  if (detail.isError || !detail.data) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <LoadErrorNotice
          error={detail.error}
          onRetry={() => void detail.refetch()}
          failedMessage="This training module could not be loaded."
          notFound={<p data-testid="training-module-not-found">Training module not found.</p>}
          testIdPrefix="training-assign"
        />
      </div>
    );
  }

  const module = detail.data;
  const assignable = module.isActive && module.status !== 'draft';
  const active = members.members.filter((m) => !m.status || m.status === 'active');

  const onSubmit = async () => {
    setResult(null);
    const done = await assign({
      moduleId: module.id,
      assigneeIds: selected,
      ...(dueDate ? { dueDate: new Date(`${dueDate}T23:59:59`).toISOString() } : {}),
    })
      .unwrap()
      .catch(() => null);
    if (done) {
      setResult(done);
      setSelected([]);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="training-assign">
      <div>
        <Link to={TRAINING_ROUTES.dashboard} className="text-sm text-primary-600 hover:underline">
          ← Training
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Assign: {module.name}</h1>
        {module.latestVersion && (
          <p className="text-sm text-gray-600">
            New assignments get <VersionLabel version={module.latestVersion.version} contentHash={module.latestVersion.contentHash} />.
            Members who already have this module keep the version they were assigned.
          </p>
        )}
      </div>

      {!assignable ? (
        <div className="card p-6 text-sm text-gray-700" data-testid="training-assign-unavailable">
          {!module.isActive
            ? 'This module is archived and cannot be assigned.'
            : 'This module has no published version yet. Publish it before assigning.'}{' '}
          <Link to={TRAINING_ROUTES.editModule(module.id)} className="text-primary-600 hover:underline">
            Open the module
          </Link>
        </div>
      ) : (
        <form
          className="card p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">Workspace members</legend>
            {members.isLoading ? (
              <p className="text-sm text-gray-500">Loading members…</p>
            ) : members.error ? (
              <div className="text-sm text-red-700" role="alert" data-testid="training-members-failed">
                Members could not be loaded from LevelUp Central.{' '}
                <button type="button" className="underline" onClick={members.refetch}>
                  Try again
                </button>
              </div>
            ) : active.length === 0 ? (
              <p className="text-sm text-gray-500">This workspace has no active members.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1">
                {active.map((m) => (
                  <label key={m.userId} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selected.includes(m.userId)}
                      onChange={() =>
                        setSelected((xs) => (xs.includes(m.userId) ? xs.filter((x) => x !== m.userId) : [...xs, m.userId]))
                      }
                    />
                    <span>{m.fullName || m.email}</span>
                    {m.fullName && <span className="text-xs text-gray-500">{m.email}</span>}
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Due date (optional)</span>
            <input type="date" className="form-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>

          <TrainingErrorNotice error={assignState.error} />

          <button type="submit" className="btn btn-primary" disabled={assignState.isLoading || selected.length === 0} data-testid="training-assign-submit">
            Assign to {selected.length} member{selected.length === 1 ? '' : 's'}
          </button>
        </form>
      )}

      {result && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status" data-testid="training-assign-result">
          {result.filter((a) => a.outcome === 'created').length} assigned
          {result.some((a) => a.outcome === 'already_assigned') &&
            ` · ${result.filter((a) => a.outcome === 'already_assigned').length} already had this module (unchanged)`}
          .
        </div>
      )}
    </div>
  );
}
